import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2, ArrowLeft, ArrowRight, BarChart3, Zap, GraduationCap, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { resumePendingCheckout } from '../lib/pendingPlan';

const AUTH_IMAGE = 'https://images.unsplash.com/photo-1637002722490-5f8ceed9774c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const GoogleIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const AuthVisual = () => (
  <div className="hidden lg:flex relative items-center justify-center min-h-[620px]" aria-hidden="true">
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 620" fill="none">
      <path d="M40 560 C 180 470, 260 520, 330 420 S 520 220, 580 140" stroke="#bfdbfe" strokeWidth="2" />
      <path d="M120 120 C 260 60, 420 80, 560 40" stroke="#dbeafe" strokeWidth="2" />
    </svg>
    <div className="absolute top-12 left-[58%] w-6 h-6 rounded-md bg-blue-500/80 float-slow" />
    <div className="absolute top-24 right-[14%] w-16 h-16 rounded-2xl bg-blue-100/70" />
    <div className="absolute bottom-40 left-[8%] w-6 h-6 rounded-md bg-blue-500/80 float-slow" style={{ animationDelay: '1s' }} />
    <div className="absolute top-[34%] right-[22%] flex flex-col gap-2">{[0, 1, 2].map(i => <span key={i} className="w-2.5 h-2.5 rounded-full bg-blue-500" />)}</div>

    <div className="relative w-[380px] fade-up">
      <div className="absolute -left-14 top-20 w-40 h-52 rounded-2xl bg-slate-700 -rotate-6 shadow-xl" />
      <div className="absolute -right-12 top-36 w-32 h-44 rounded-2xl bg-slate-300 rotate-6 shadow-xl" />
      <div className="relative rounded-[28px] bg-white p-3 shadow-2xl shadow-blue-900/15 -rotate-3">
        <img src={AUTH_IMAGE} alt="" className="w-full aspect-[4/3.4] object-cover rounded-[20px]" />
      </div>
      <div className="absolute -top-8 -left-8 w-24 h-24 rounded-3xl bg-white shadow-xl flex items-center justify-center float-slow">
        <BarChart3 className="w-11 h-11 text-blue-600" strokeWidth={2.5} />
      </div>
      <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-3xl bg-white shadow-xl flex items-center justify-center float-slow" style={{ animationDelay: '1.5s' }}>
        <Zap className="w-11 h-11 text-indigo-600 fill-indigo-600" />
      </div>
    </div>

    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-16">
      {[[GraduationCap, 'bg-blue-50 text-blue-600'], [BarChart3, 'bg-indigo-50 text-indigo-500'], [Users, 'bg-emerald-50 text-emerald-500']].map(([Ic, c], i) => (
        <div key={i} className={`w-20 h-20 rounded-2xl ${c} flex items-center justify-center shadow-sm`}><Ic className="w-8 h-8" /></div>
      ))}
    </div>
  </div>
);

const Field = ({ icon: Ic, type = 'text', value, onChange, placeholder, testId, toggle, minLength }) => {
  const [show, setShow] = useState(false);
  const isPwd = type === 'password';
  return (
    <div className="relative">
      <input
        data-testid={testId}
        required
        minLength={minLength}
        type={isPwd && show ? 'text' : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full h-14 sm:h-[60px] rounded-2xl bg-white border border-slate-200 ${isPwd && toggle ? 'ps-14' : 'ps-5'} pe-14 text-slate-900 placeholder:text-slate-400 text-sm sm:text-base outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-[border-color,box-shadow]`}
      />
      <Ic className="absolute top-1/2 -translate-y-1/2 end-5 w-5 h-5 text-slate-500 pointer-events-none" />
      {isPwd && toggle && (
        <button type="button" data-testid={`${testId}-toggle`} onClick={() => setShow(s => !s)} className="absolute top-1/2 -translate-y-1/2 start-4 p-1 text-slate-500 hover:text-slate-800 transition-colors" aria-label="toggle password">
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  );
};

const AuthPage = ({ mode }) => {
  const { lang, isRTL } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const L = (ar, en) => tx(lang, ar, en);
  const isLogin = mode === 'login';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const Chevron = isRTL ? ChevronRight : ChevronLeft;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(isLogin ? L('مرحباً بعودتك!', 'Welcome back!') : L('تم إنشاء حسابك بنجاح', 'Account created'));
      if (await resumePendingCheckout()) return;
      navigate(isLogin && u.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const known = {
        'Invalid credentials': L('البريد الإلكتروني أو كلمة المرور غير صحيحة', 'Invalid email or password'),
        'Email already registered': L('هذا البريد مسجّل مسبقاً، سجّل الدخول بدلاً من ذلك', 'This email is already registered, please log in'),
      };
      toast.error(known[detail] || detail || (isLogin ? L('فشل تسجيل الدخول', 'Login failed') : L('فشل إنشاء الحساب', 'Registration failed')));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google';
    try {
      const { data } = await api.get('/auth/google/url', { params: { redirect_uri: redirectUri } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(L('تعذر بدء تسجيل الدخول بجوجل، حاول مرة أخرى', 'Could not start Google sign-in, please try again'));
    }
  };

  const tab = (active) => `flex-1 h-11 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center font-bold text-sm sm:text-base transition-colors ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25' : 'text-slate-800 hover:text-blue-700'}`;

  return (
    <main data-testid={`auth-page-${mode}`} className="relative min-h-screen pt-24 sm:pt-28 pb-16 bg-gradient-to-br from-blue-50 via-slate-50 to-white overflow-hidden text-slate-900">
      <div className="site-container grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="w-full max-w-[530px] mx-auto lg:mx-0 bg-white/90 backdrop-blur-xl rounded-[28px] sm:rounded-[32px] border border-slate-100 shadow-2xl shadow-blue-900/10 p-5 sm:p-9 fade-up">
          <div className="flex gap-2 bg-slate-100 rounded-2xl p-1.5 mb-7">
            <Link to="/subscription" data-testid="auth-tab-register" className={tab(!isLogin)}>{L('إنشاء حساب', 'Sign Up')}</Link>
            <Link to="/login" data-testid="auth-tab-login" className={tab(isLogin)}>{L('تسجيل الدخول', 'Log In')}</Link>
          </div>

          <button type="button" data-testid="auth-google-btn" onClick={googleLogin} className="group w-full h-14 sm:h-[60px] rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md flex items-center justify-between px-5 transition-[border-color,box-shadow]">
            <GoogleIcon />
            <span className="font-bold text-slate-900 text-sm sm:text-base">{L('المتابعة باستخدام Google', 'Continue with Google')}</span>
            <Chevron className="w-5 h-5 text-slate-800 transition-transform group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5" />
          </button>

          <div className="flex items-center gap-4 my-7">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-slate-500 text-sm">{L('أو', 'or')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && <Field icon={User} value={form.name} onChange={upd('name')} placeholder={L('الاسم الكامل', 'Full name')} testId="auth-name-input" />}
            <Field icon={Mail} type="email" value={form.email} onChange={upd('email')} placeholder={L('البريد الإلكتروني', 'Email address')} testId="auth-email-input" />
            <Field icon={Lock} type="password" toggle minLength={isLogin ? undefined : 6} value={form.password} onChange={upd('password')} placeholder={L('كلمة المرور', 'Password')} testId="auth-password-input" />
            <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="group w-full h-14 sm:h-[60px] mt-3 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white font-bold text-base sm:text-lg flex items-center justify-center gap-4 shadow-lg shadow-blue-600/25 transition-colors">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  {isLogin ? L('تسجيل الدخول', 'Log In') : L('إنشاء حساب', 'Create Account')}
                  <Arrow className="w-5 h-5 transition-transform group-hover:-translate-x-1 rtl:group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-slate-600 text-sm sm:text-base mt-7">
            {isLogin ? L('ليس لديك حساب؟', "Don't have an account?") : L('لديك حساب بالفعل؟', 'Already have an account?')}{' '}
            <Link to={isLogin ? '/subscription' : '/login'} data-testid="auth-switch-link" className="text-blue-700 font-bold hover:underline">
              {isLogin ? L('إنشاء حساب', 'Sign Up') : L('تسجيل الدخول', 'Log In')}
            </Link>
          </p>
        </div>

        <AuthVisual />
      </div>
    </main>
  );
};

export default AuthPage;
